import React from "react";
import { NodeOrEdge } from "@hex-insights/app-modules";
import {
	Button,
	ClassNameProps,
	Column,
	Conditional,
	Else,
	Heading,
	HeadingLevel,
	If,
	Modal,
	Row,
	Section,
	StyleProps,
} from "@hex-insights/core";
import { EmptyView, SlimEmptyView } from "../../../../Components/Empty";
import { Icon } from "../../../../Components/Icon";
import { Tile } from "../../../../Components/Tile";
import { asNode } from "../../../../Utilities";
import { CardContract, ContractCard } from "../Card";
import { ContractCreateModal, ContractCreateModalProps } from "../CreateModal";

export type ContractListSectionProps = {
	variant: "admin" | "parent";
	isLoading?: boolean;
	contracts: NodeOrEdge<CardContract>[] | null | undefined;
	Container?: typeof Tile | typeof Section;
	headingLevel?: HeadingLevel;
	emptyView?: "slim" | "full";
	initialCreateFormValues?: ContractCreateModalProps["initialFormValues"];
} & Partial<ClassNameProps & StyleProps>;

export function ContractListSection({
	variant,
	isLoading = false,
	contracts,
	Container = Section,
	headingLevel = 3,
	emptyView = "slim",
	initialCreateFormValues,
	className,
	style,
}: ContractListSectionProps) {
	const { isModalOpen, openModal, closeModal } = Modal.useToggle();

	return (
		<React.Fragment>
			<Container className={className} style={style}>
				<Container.Header>
					<Row justify="space-between" align="center">
						<Heading level={headingLevel} noMargin>
							Contracts
						</Heading>

						<If condition={variant === "admin"}>
							<Button variant="link" size="small" onClick={openModal}>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
									<Icon.Plus size={16} /> New Contract
								</Row>
							</Button>
						</If>
					</Row>
				</Container.Header>
				<Container.Body>
					<Conditional>
						<If condition={isLoading && !contracts}>Loading...</If>
						<If condition={!!contracts && contracts.length === 0}>
							<Conditional>
								<If condition={emptyView === "slim"}>
									<SlimEmptyView>No contracts listed</SlimEmptyView>
								</If>
								<Else>
									<EmptyView
										IconComponent={Icon.Scroll}
										primaryText="Contracts"
										secondaryText="No contracts listed"
										style={{ height: "100%" }}
									/>
								</Else>
							</Conditional>
						</If>
						<Else>
							<Column justify="spaced-start">
								{contracts?.map((e) => {
									const contract = asNode(e);
									return <ContractCard key={contract.id} variant={variant} contract={contract} />;
								})}
							</Column>
						</Else>
					</Conditional>
				</Container.Body>
			</Container>

			<Modal.If condition={isModalOpen}>
				<ContractCreateModal initialFormValues={initialCreateFormValues} onClose={closeModal} onSuccess={closeModal} />
			</Modal.If>
		</React.Fragment>
	);
}
